import {
  createMorphemeRouteBodyWindow,
  getMorphemeBackDestination,
} from "#morpheme-route-body-window";
import {
  WindowContext,
  RouteBodyContext,
  BodyReadyContext,
  type BodyWindow,
  type BodyRoute,
} from "#morpheme-route-body-context";
import { Stack, useNavigation } from "@-/com.npmjs.expo-router";
import {
  useNavigationState,
  useRoute,
  usePreventRemove,
} from "@-/com.npmjs.expo-router/react-navigation";
import { RouteBodyStateProvider } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { NavigationQueryActivityContext } from "@-/com.npmjs.tanstack__react-query";
import {
  useCallback,
  useContext,
  useInsertionEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ComponentProps,
  type ReactNode,
} from "react";

/** Native history stays intact. Only each route's expensive layout body has a bounded lifetime. */
export function MorphemeRouteStack(props: ComponentProps<typeof Stack>) {
  const [owner] = useState(createMorphemeRouteBodyWindow);
  const isFocused = useIsNavigationFocused();
  const value = useMemo(() => ({ isFocused, owner }), [isFocused, owner]);
  return (
    <WindowContext value={value}>
      <Stack {...props} />
    </WindowContext>
  );
}

function ManagedMorphemeRouteShell({
  children,
  isFocused,
  owner,
}: {
  children: ReactNode;
  isFocused: boolean;
  owner: BodyWindow;
}) {
  const navigation = useNavigation();
  const route = useRoute();
  const state = useNavigationState((value) => value);
  useSyncExternalStore(owner.subscribe, owner.getSnapshot, owner.getSnapshot);
  const routeIndex = state.routes.findIndex((entry) => entry.key === route.key);
  const active =
    isFocused &&
    (routeIndex >= Math.max(0, state.index - 1) || owner.getPendingTarget() === route.key);
  const previous = state.routes[state.index - 1];
  const canGoBack =
    previous === undefined || previous.name !== "[morpheme]" || owner.isReady(previous.key);
  const isCurrentRoute = state.routes[state.index]?.key === route.key;
  // This also sets native-stack's preventNativeDismiss flag. The hook guards hardware Back
  // and the native left-edge gesture while reacquiring the destination's underlay.
  usePreventRemove(isCurrentRoute && !canGoBack, ({ data, repeat }) => {
    const current = navigation.getState();
    const target =
      current === undefined ? undefined : getMorphemeBackDestination(current, data.action);
    if (target === undefined || owner.isReady(target)) {
      repeat();
      return;
    }
    owner.requestBack({ action: data.action, dispatch: repeat, source: route.key, target });
  });

  useLayoutEffect(() => {
    owner.reconcile(
      state.routes.map((entry) => entry.key),
      isFocused ? state.routes[state.index]?.key : undefined,
    );
  }, [isFocused, owner, state.index, state.routes]);

  const value = useMemo(() => ({ active, owner, routeKey: route.key }), [active, owner, route.key]);
  return (
    <RouteBodyContext value={value}>
      <RouteBodyStateProvider>{children}</RouteBodyStateProvider>
    </RouteBodyContext>
  );
}

export function MorphemeRouteShell({ children }: { children: ReactNode }) {
  const window = useContext(WindowContext);
  return window === null ? (
    children
  ) : (
    <ManagedMorphemeRouteShell {...window}>{children}</ManagedMorphemeRouteShell>
  );
}

function MountedMorphemeRouteBody({ children, route }: { children: ReactNode; route: BodyRoute }) {
  const [epoch] = useState(() => Symbol("morpheme-body"));
  const onReady = useCallback(
    () => route.owner.markReady(route.routeKey, epoch),
    [epoch, route.owner, route.routeKey],
  );
  // Register before any descendant layout effect can report readiness. This phase only mutates
  // the epoch registry; it never notifies subscribers or schedules a React update.
  useInsertionEffect(() => {
    route.owner.mount(route.routeKey, epoch);
  }, [epoch, route.owner, route.routeKey]);
  useLayoutEffect(() => {
    return () => route.owner.release(route.routeKey, epoch);
  }, [epoch, route.owner, route.routeKey]);
  return (
    <BodyReadyContext value={onReady}>
      <NavigationQueryActivityContext value={true}>{children}</NavigationQueryActivityContext>
    </BodyReadyContext>
  );
}

/** Place inside navigator.layout, above all slice reads and native pager/view descendants. */
export function MorphemeRouteBody({ children }: { children: ReactNode }) {
  const route = useContext(RouteBodyContext);
  if (route === null) {
    return children;
  }
  if (!route.active) {
    // oxlint-disable-next-line haniverse/no-return-null -- kept-alive host: an inactive retained route keeps its shell (and `RouteBodyContext`) mounted but deliberately unmounts its body until reactivated.
    return null;
  }
  return <MountedMorphemeRouteBody route={route}>{children}</MountedMorphemeRouteBody>;
}
